/*
 * Copyright (c) 2021 Signum+ Software & Facility GmbH
 * Author: sebastian.woess@signum-plus.at
 */

import { ViewComponentKey } from '../key/view-component.key';
import { ComponentFieldKey, ComponentData, ComponentModel } from '../model/component.model';
import { ContextBinding, ContextBindings } from '../model/context-binding.model';
import { ViewErrorKey } from '../key/view-error.key';
import { ApplicationError, ChangedEvent, ObjectAdapter } from '@typescript-kit/core';
import { Observable, Subscription } from 'rxjs';

export interface CheckboxData extends ComponentData {
  label?: string | boolean | ComponentData;
  labelPosition?: 'left' | 'right';
  checked?: boolean;
  scope?: string;
  context?: object;
  contextBinding?: ContextBinding;
  onCheckedChanged?: (isChecked: boolean, model: CheckboxModel) => void;
}

export class CheckboxFieldKey {
  static readonly LABEL = 'label';
  static readonly LABEL_POSITION = 'labelPosition';
  static readonly CHECKED = 'checked';
  static readonly SCOPE = 'scope';
  static readonly CONTEXT = 'context';
  static readonly CONTEXT_BINDING = 'contextBinding';
  static readonly ON_CHECKED_CHANGED = 'onCheckedChanged';
}

export class CheckboxModel extends ComponentModel implements CheckboxData {
  static readonly KEYS = ComponentModel.KEYS.concat(
    Object.keys(CheckboxFieldKey).map((key) => CheckboxFieldKey[key])
  );

  private contextChangedSubscription: Subscription;

  constructor(data?: CheckboxData, keys?: string[]) {
    super(data, keys || CheckboxModel.KEYS);
  }

  get label(): string | boolean | ComponentModel {
    return this.get(CheckboxFieldKey.LABEL);
  }

  set label(value: string | boolean | ComponentModel) {
    this.set(CheckboxFieldKey.LABEL, value);
  }

  get labelPosition(): 'left' | 'right' {
    return this.get(CheckboxFieldKey.LABEL_POSITION, 'right');
  }

  set labelPosition(value: 'left' | 'right') {
    this.set(CheckboxFieldKey.LABEL_POSITION, value);
  }

  get scope(): string {
    return this.get(CheckboxFieldKey.SCOPE);
  }

  set scope(value: string) {
    this.set(CheckboxFieldKey.SCOPE, value);
  }

  get checked(): boolean {
    return this.get(CheckboxFieldKey.CHECKED, false);
  }

  set checked(value: boolean) {
    this.set(CheckboxFieldKey.CHECKED, value);
  }

  get context(): object {
    return this.get(CheckboxFieldKey.CONTEXT);
  }

  set context(value: object) {
    this.set(CheckboxFieldKey.CONTEXT, value);
  }

  get contextBinding(): ContextBinding {
    return this.get(CheckboxFieldKey.CONTEXT_BINDING);
  }

  set contextBinding(value: ContextBinding) {
    this.set(CheckboxFieldKey.CONTEXT_BINDING, value);
  }

  get onCheckedChanged(): (isChecked: boolean, model: CheckboxModel) => void {
    return this.get(CheckboxFieldKey.ON_CHECKED_CHANGED);
  }

  protected setupValues(values: CheckboxData, exclusive: boolean): CheckboxData {
    if (exclusive && !(ComponentFieldKey.TYPE in values)) {
      values.type = ViewComponentKey.CHECKBOX;
    }
    if (exclusive && !(ComponentFieldKey.TAB_INDEX in values)) {
      values.tabIndex = 0;
    }

    this.setupContextBinding(values, exclusive);

    this.setupChild(CheckboxFieldKey.LABEL, values, exclusive);
    return super.setupValues(values, exclusive) as CheckboxData;
  }

  protected setupContextBinding(values: CheckboxData, exclusive: boolean): void {
    if (!exclusive
      && !(CheckboxFieldKey.CONTEXT in values)
      && !(CheckboxFieldKey.CONTEXT_BINDING in values)
      && !(ComponentFieldKey.KEY in values)
    ) {
      return;
    }
    if (exclusive && values.contextBinding === undefined) {
      values.contextBinding = ContextBindings.NONE;
    }
    const key: string = this.getSetupValue(values, ComponentFieldKey.KEY, exclusive);
    const context: object = this.getSetupValue(values, CheckboxFieldKey.CONTEXT, exclusive);
    const contextBinding: ContextBinding = this.getSetupValue(values, CheckboxFieldKey.CONTEXT_BINDING, exclusive);
    if (this.context === context && this.contextBinding === contextBinding && this.key === key) {
      return;
    }
    if (this.contextChangedSubscription) {
      this.contextChangedSubscription.unsubscribe();
      this.contextChangedSubscription = null;
    }
    if (!context || contextBinding === ContextBindings.NONE) {
      return;
    }
    if (!key) {
      throw new ApplicationError({
        key: ViewErrorKey.COMPONENT_KEY_MISSING,
        message: 'Component key missing'
      });
    }
    if (typeof context !== 'object') {
      throw new ApplicationError({
        key: ViewErrorKey.COMPONENT_CONTEXT_INVALID,
        arguments: { context },
        message: 'Component context invalid'
      });
    }
    switch (contextBinding) {
      case ContextBindings.MODEL_TO_CONTEXT:
        break;
      case ContextBindings.CONTEXT_TO_MODEL:
      case ContextBindings.TWO_WAY:
        const changedEvent: Observable<ChangedEvent> = context['changedEvent'];
        if (!changedEvent || !(changedEvent instanceof Observable)) {
          throw new ApplicationError({
            key: ViewErrorKey.COMPONENT_CONTEXT_INVALID,
            arguments: { context },
            message: 'Component context invalid'
          });
        }
        this.contextChangedSubscription = changedEvent.subscribe((event: ChangedEvent) => {
          if (key in event.changes) {
            this.checked = event.changes[key].currentValue;
          }
        });
        if (contextBinding === ContextBindings.CONTEXT_TO_MODEL || !(CheckboxFieldKey.CHECKED in values)) {
          values.value = ObjectAdapter.from(context).get(key);
        }
        break;
      default:
        throw new ApplicationError({
          key: ViewErrorKey.COMPONENT_CONTEXT_BINDING_INVALID,
          arguments: { contextBinding },
          message: `Component context binding '${contextBinding}' invalid`
        });
    }
  }

  protected fireChangedEvent(changedEvent: ChangedEvent): void {
    const changes = changedEvent.changes;
    const checkedChange = changes[CheckboxFieldKey.CHECKED];
    if (this.context && (this.contextBinding === ContextBindings.TWO_WAY || this.contextBinding === ContextBindings.MODEL_TO_CONTEXT)) {
      if (checkedChange
        || changes[CheckboxFieldKey.CONTEXT]
        || changes[CheckboxFieldKey.CONTEXT_BINDING]
        || changes[ComponentFieldKey.KEY]
      ) {
        ObjectAdapter.from(this.context).set(this.key, this.checked);
      }
    }
    if (checkedChange && this.onCheckedChanged) {
      this.onCheckedChanged(checkedChange.currentValue, this);
    }
    super.fireChangedEvent(changedEvent);
  }
}
